import React, { useEffect, useState } from 'react';
import './Home.css';
import { useParams, useNavigate } from 'react-router-dom';
import ShareIcon from '../assets/ShareIcon';

export default function ItemsPage() {
  const { categoryId, categoryName } = useParams();
  const navigate = useNavigate();
  const [cakes, setCakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchedCategoryName, setFetchedCategoryName] = useState(null);
  const [sharedMsg, setSharedMsg] = useState(false);

  useEffect(() => {
    if (!categoryId) return;
    console.log('Fetching cakes for category:', categoryId);
    fetch(`https://devbite.vercel.app/api/cakedoctorcakes?category=${encodeURIComponent(categoryId)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch cakes');
        return res.json();
      })
      .then((data) => {
        setCakes(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [categoryId]);

  useEffect(() => {
    if (!categoryName && categoryId) {
      fetch(`https://devbite.vercel.app/api/cakedoctorcategories`)
        .then(res => res.json())
        .then(data => {
          const cat = (data.categories || data) && (data.categories || data).find(c => c.id === categoryId);
          if (cat && cat.name) {
            setFetchedCategoryName(cat.name);
          }
        });
    }
  }, [categoryId, categoryName]);

  const handleCakeClick = (cake) => {
    // Navigate with categoryId and cakeId in the URL
    navigate(`/cake/${categoryId}/${cake.id}`);
  };

  const displayCategoryName = categoryName || fetchedCategoryName || 'Category';

  // Price filter options
  const priceFilters = [
    { label: 'All', value: 'all' },
    { label: '50,000', value: 50000 },
    { label: '60,000', value: 60000 },
    { label: '70,000', value: 70000 },
    { label: '80,000', value: 80000 },
    { label: '90,000', value: 90000 },
    { label: '100,000', value: 100000 },
    { label: '110,000 - 200,000', value: [110000, 200000] },
    { label: '210,000 - 310,000', value: [210000, 310000] },
    { label: '320,000 - 420,000', value: [320000, 420000] },
    { label: '430,000 - 530,000', value: [430000, 530000] },
    { label: '540,000 - 640,000', value: [540000, 640000] },
    { label: '650,000 - 750,000', value: [650000, 750000] },
    { label: '760,000 - 860,000', value: [760000, 860000] },
    { label: '870,000 - 970,000', value: [870000, 970000] },
    { label: '980,000 - 1,080,000', value: [980000, 1080000] },
    { label: 'Above 1,080,000', value: [1080001, 999999999] },
  ];

  // --- Helper to get price filter from URL ---
  function getInitialPriceFilter() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('price')) {
      return Number(params.get('price'));
    } else if (params.has('min_price') && params.has('max_price')) {
      return [Number(params.get('min_price')), Number(params.get('max_price'))];
    } else {
      return 'all';
    }
  }

  const [selectedPrice, setSelectedPrice] = useState(getInitialPriceFilter);
  const [priceInitialized, setPriceInitialized] = useState(false);

  // Helper to build API URL based on selected price filter
  const buildApiUrl = () => {
    let url = `https://devbite.vercel.app/api/cakedoctorcakes?category=${encodeURIComponent(categoryId)}`;
    if (selectedPrice !== 'all') {
      if (Array.isArray(selectedPrice)) {
        url += `&min_price=${selectedPrice[0]}&max_price=${selectedPrice[1]}`;
      } else {
        url += `&price=${selectedPrice}`;
      }
    }
    return url;
  };

  // On mount, read price filter from URL and set selectedPrice
  useEffect(() => {
    setSelectedPrice(getInitialPriceFilter());
    setPriceInitialized(true);
    // eslint-disable-next-line
  }, [categoryId]);

  // Fetch cakes when category or price filter changes, but only after priceInitialized
  useEffect(() => {
    if (!categoryId || !priceInitialized) return;
    setLoading(true);
    setError(null);
    fetch(buildApiUrl())
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch cakes');
        return res.json();
      })
      .then((data) => {
        setCakes(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [categoryId, selectedPrice, priceInitialized]);

  // Update URL with price filter (for sharing and navigation)
  useEffect(() => {
    if (!categoryId || !displayCategoryName || displayCategoryName === 'Category' || !priceInitialized) return;
    let query = '';
    if (selectedPrice !== 'all') {
      if (Array.isArray(selectedPrice)) {
        query = `?min_price=${selectedPrice[0]}&max_price=${selectedPrice[1]}`;
      } else {
        query = `?price=${selectedPrice}`;
      }
    }
    const basePath = `/items/${categoryId}/${encodeURIComponent(displayCategoryName)}`;
    const newUrl = basePath + query;
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [categoryId, selectedPrice, displayCategoryName, priceInitialized]);

  // Share handler for category
  const handleShareCategory = async () => {
    // Build share URL with price filter
    let query = '';
    if (selectedPrice !== 'all') {
      if (Array.isArray(selectedPrice)) {
        query = `?min_price=${selectedPrice[0]}&max_price=${selectedPrice[1]}`;
      } else {
        query = `?price=${selectedPrice}`;
      }
    }
    const categoryUrl = `${window.location.origin}/items/${categoryId}/${encodeURIComponent(displayCategoryName)}${query}`;
    // Pick one cake image as thumbnail
    const cakeImg = cakes && cakes.length > 0 && cakes[0].imageUrls && cakes[0].imageUrls.length > 0 ? cakes[0].imageUrls[0] : '';
    const shareData = {
      title: `Check out this category: ${decodeURIComponent(displayCategoryName)}`,
      text: `Shared with you: ${decodeURIComponent(displayCategoryName)}`,
      url: categoryUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setSharedMsg(true);
      } catch (err) {
        // fallback if needed
      }
    } else {
      // fallback: copy link
      await navigator.clipboard.writeText(categoryUrl);
      setSharedMsg(true);
      alert('Category link copied to clipboard!');
    }
  };

  return (
    <div className="items-root">
      <div className="items-header">
        <button className="back-btn" onClick={() => navigate('/')}>←</button>
        <span className="items-title">{decodeURIComponent(displayCategoryName)}</span>
        {/* Share button */}
        <button
          className="share-btn"
          onClick={handleShareCategory}
          title="Share this category"
          style={{ background: 'none', border: 'none', marginLeft: 'auto', marginRight: 18, cursor: 'pointer' }}
        >
          <ShareIcon size={26} />
        </button>
      </div>
      {/* Price Filter Bar */}
      <div className="price-filter-bar">
        <div className="price-filter-scroll">
          {priceFilters.map((filter, idx) => (
            <button
              key={filter.label}
              className={`price-filter-btn${selectedPrice === filter.value || (Array.isArray(filter.value) && Array.isArray(selectedPrice) && filter.value[0] === selectedPrice[0] && filter.value[1] === selectedPrice[1]) ? ' selected' : ''}`}
              onClick={() => setSelectedPrice(filter.value)}
              style={{ transitionDelay: `${idx * 0.04}s` }}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>
      {/* Shared message and thumbnail */}
      {sharedMsg && cakes && cakes.length > 0 && (
        <div className="shared-category-message" style={{
          background: '#ffe3ed',
          borderRadius: 14,
          margin: '18px auto 0 auto',
          maxWidth: 340,
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 16
        }}>
          <a href={`/cake/${categoryId}/${cakes[0].id}`} style={{ display: 'block', minWidth: 70 }}>
            <img src={cakes[0].imageUrls && cakes[0].imageUrls[0]} alt={cakes[0].name} style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 12, boxShadow: '0 2px 8px #e85d9e33' }} />
          </a>
          <div>
            <div style={{ fontWeight: 700, color: '#e85d9e', marginBottom: 4 }}>Category shared!</div>
            <div style={{ fontSize: 13, color: '#444' }}>{decodeURIComponent(displayCategoryName)}</div>
          </div>
        </div>
      )}
      <div className="cakes-grid">
        {loading ? (
          <div className="cake-card" style={{ minWidth: 170 }}>Loading...</div>
        ) : error ? (
          <div className="cake-card" style={{ minWidth: 170, color: 'red' }}>{error}</div>
        ) : cakes.length === 0 ? (
          <div className="cake-card" style={{ minWidth: 170 }}>No cakes found</div>
        ) : (
          cakes.map((cake) => (
            <div className="cake-card" key={cake.id} onClick={() => handleCakeClick(cake)} style={{ cursor: 'pointer' }}>
              <img
                src={cake.imageUrls && cake.imageUrls.length > 0 ? cake.imageUrls[0] : ''}
                alt={cake.name}
                className="cake-img"
              />
              <div className="cake-name">{cake.name}</div>
              {cake.price && (
                <div className="cake-price">UGX {cake.price}</div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
